import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants';
import { readStoredUser } from '../../utils/navbarUser';
import './StreamCard.css';

function buildTwitchPreviewUrl(twitchLogin) {
  const login = String(twitchLogin || '').trim().toLowerCase();
  if (!login) return null;
  const bucket = Math.floor(Date.now() / 30000);
  return `https://static-cdn.jtvnw.net/previews-ttv/live_user_${encodeURIComponent(login)}-640x360.jpg?cb=${bucket}`;
}

/**
 * Stream card for the "Go Live!" call-to-action.
 *
 * Displays a stream thumbnail with viewer count, streamer info,
 * and content tags. Used for both "Most Watched" and "Random Stream"
 * card types.
 *
 * @param {Object} props
 * @param {string} props.title - Card header title
 * @param {string} props.streamer - Streamer's display name
 * @param {string} props.category - Stream category (e.g., "Music", "Gaming")
 * @param {string} props.viewers - Formatted viewer count
 * @param {string|null} props.thumbnail - Thumbnail image URL
 * @param {string[]} props.tags - Content tags array
 * @returns {JSX.Element}
 */
function StreamCard({ title, streamer, streamerAvatar, category, viewers, thumbnail, tags = [] }) {
  return (
    <article className="stream-card">
      {/* Header with "Go Live!" highlight */}
      <h3 className="stream-card__header">
        <span className="stream-card__go-live">Go Live!</span>
        <span className="stream-card__header-text">
          {title.replace('Go Live!', '').trim()}
        </span>
      </h3>

      {/* Thumbnail */}
      <div className="stream-card__thumbnail">
        {thumbnail ? (
          <img src={thumbnail} alt={`${streamer}'s stream`} />
        ) : (
          <div className="stream-card__thumbnail-placeholder">
            <span>🎬</span>
          </div>
        )}

        {/* Viewer Badge */}
        <div className="stream-card__viewers">
          {viewers} viewers
        </div>
      </div>

      {/* Streamer Info */}
      <div className="stream-card__info">
        <div className="stream-card__avatar">
          {streamerAvatar ? (
            <img src={streamerAvatar} alt="" referrerPolicy="no-referrer" />
          ) : (
            <span className="stream-card__avatar-placeholder">
              {streamer.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="stream-card__details">
          <span className="stream-card__streamer">{streamer}</span>
          <span className="stream-card__category">{category}</span>
        </div>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="stream-card__tags">
          {tags.map((tag, index) => (
            <span key={index} className="stream-card__tag">
              {tag}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}

StreamCard.propTypes = {
  title: PropTypes.string.isRequired,
  streamer: PropTypes.string.isRequired,
  streamerAvatar: PropTypes.string,
  category: PropTypes.string.isRequired,
  viewers: PropTypes.string.isRequired,
  thumbnail: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Mystery Stream card variant.
 *
 * A special card type that entices users with the unknown.
 * Displays a pulsing "?" instead of stream details.
 *
 * @returns {JSX.Element}
 */
/**
 * Live stream card — shown for streamers currently broadcasting.
 * Clicking navigates to the streamer's profile page.
 */
export function LiveStreamCard({ stream }) {
  const navigate = useNavigate();
  const currentUser = readStoredUser();
  const {
    username,
    displayName,
    avatarUrl,
    platform,
    viewerCount,
    twitchLogin,
  } = stream || {};
  const [thumbFailed, setThumbFailed] = useState(false);
  const thumbnailUrl = !thumbFailed && platform === 'twitch' ? buildTwitchPreviewUrl(twitchLogin) : null;

  const handleOpen = () => {
    if (!username) return;
    if (currentUser?.username === username) {
      navigate(ROUTES.GO_LIVE);
      return;
    }

    navigate(ROUTES.WATCH.replace(':username', encodeURIComponent(username)));
  };

  const handleKey = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleOpen();
    }
  };

  const displayed = username || displayName || 'Streamer';
  const viewersLabel = typeof viewerCount === 'number' ? viewerCount : 0;

  return (
    <article
      className="stream-card stream-card--live"
      onClick={handleOpen}
      onKeyDown={handleKey}
      role="button"
      tabIndex={0}
      aria-label={`${currentUser?.username === username ? 'Manage' : 'Watch'} ${displayed} live`}
    >
      <h3 className="stream-card__header">
        <span className="stream-card__live-badge">LIVE</span>
        <span className="stream-card__header-text">
          {`${displayed} is live`}
        </span>
      </h3>

      <div className="stream-card__thumbnail">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={`${displayed}'s stream preview`}
            onError={() => setThumbFailed(true)}
          />
        ) : (
          <div className="stream-card__thumbnail-placeholder">
            <span>🔴</span>
          </div>
        )}
        <div className="stream-card__viewers">
          {viewersLabel} viewers
        </div>
      </div>

      <div className="stream-card__info">
        <div className="stream-card__avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" referrerPolicy="no-referrer" />
          ) : (
            <span className="stream-card__avatar-placeholder">
              {displayed.charAt(0).toUpperCase()}
            </span>
          )}
        </div>
        <div className="stream-card__details">
          <span className="stream-card__streamer">{displayed}</span>
          <span className="stream-card__category">
            {platform ? `via ${platform}` : 'Streaming'}
          </span>
        </div>
      </div>
    </article>
  );
}

LiveStreamCard.propTypes = {
  stream: PropTypes.shape({
    userId: PropTypes.string,
    username: PropTypes.string,
    displayName: PropTypes.string,
    avatarUrl: PropTypes.string,
    streamTitle: PropTypes.string,
    platform: PropTypes.string,
    viewerCount: PropTypes.number,
    twitchLogin: PropTypes.string,
  }).isRequired,
};

export function MysteryStreamCard() {
  return (
    <article className="stream-card stream-card--mystery">
      <h3 className="stream-card__header stream-card__header--centered">
        Mystery Stream
      </h3>

      <div className="stream-card__thumbnail stream-card__thumbnail--mystery">
        <span className="stream-card__mystery-icon">?</span>
      </div>
    </article>
  );
}

export default StreamCard;
